import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, AuthResponse } from '../types';
import { getAccessToken, getStoredUser, setStoredUser, storeAuth, clearAuth, onSessionExpired, isAdmin as checkIsAdmin } from '../auth';
import * as authRepository from '../repositories/authRepository';

interface UserContextValue {
  user: User | null;
  isLoggedIn: boolean;
  /** True only when the user is an admin AND admin mode is switched on. Gate
   *  every admin affordance on this (issue #889). */
  isAdmin: boolean;
  /** The raw capability: this user *may* enter admin mode. */
  isAdminUser: boolean;
  adminMode: boolean;
  toggleAdminMode: () => void;
  login: (data: AuthResponse) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextValue | null>(null);

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}

/**
 * Raw admin capability, safe outside a UserProvider (returns false) so shared
 * hooks can consult it without forcing every test to mount the provider.
 */
export function useIsAdminUser(): boolean {
  return !!useContext(UserContext)?.isAdminUser;
}

/**
 * The admin-mode switch, safe outside a UserProvider (off, and toggling is a
 * no-op) so plain component tests can render chrome that draws it.
 */
export function useAdminMode(): { adminMode: boolean; toggleAdminMode: () => void } {
  const ctx = useContext(UserContext);
  return {
    adminMode: !!ctx?.adminMode,
    toggleAdminMode: ctx?.toggleAdminMode ?? (() => undefined),
  };
}

export function UserProvider({
  children,
  initialAdminMode = false,
}: {
  children: React.ReactNode;
  /** The mode this provider starts in. Nothing in the app passes it -- the app
   *  always starts in the reader's view -- but a test that needs to render the
   *  admin view has no way to press 'a' in a static render. */
  initialAdminMode?: boolean;
}) {
  const [user, setUser] = useState<User | null>(getStoredUser());
  // Every load starts in the reader's view, admins included: admin mode is
  // per-tab state that nothing remembers. The reason is load cost, not
  // preference -- the feed's first paint asks the server for no per-user and no
  // admin data, and a remembered "on" would put that work back on the critical
  // path of every page load an admin makes. Turning it on is one keystroke.
  const [adminMode, setAdminMode] = useState(initialAdminMode);
  const navigate = useNavigate();

  const toggleAdminMode = useCallback(() => {
    setAdminMode((on) => !on);
  }, []);

  const login = useCallback((data: AuthResponse) => {
    const u = storeAuth(data);
    setUser(u);
  }, []);

  const logoutAndRedirect = useCallback(() => {
    clearAuth();
    setUser(null);
    setAdminMode(false);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    onSessionExpired(logoutAndRedirect);
  }, [logoutAndRedirect]);

  useEffect(() => {
    if (!getAccessToken()) return;
    authRepository.fetchCurrentUser()
      .then((u) => {
        // setStoredUser owns the persisted slot: it re-attaches the
        // JWT-derived flags /api/me omits (notably is_super_admin).
        setUser(setStoredUser(u));
      })
      .catch(() => { /* fetchWithAuth handles session expiry */ });
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      isLoggedIn: !!getAccessToken(),
      isAdmin: checkIsAdmin(user) && adminMode,
      isAdminUser: checkIsAdmin(user),
      adminMode,
      toggleAdminMode,
      login,
      logout: logoutAndRedirect,
    }}>
      {children}
    </UserContext.Provider>
  );
}
